import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowLeft, Zap, Phone, Euro, Wrench, Clock, AlertTriangle, CheckCircle } from 'lucide-react';
import useSEO from '../../hooks/useSEO';

const PrecioCambioBateriaBarcelona = () => {
  const navigate = useNavigate();

  useSEO({
    title: 'Precio Cambio Batería Coche Barcelona 2026 | Cuánto Cuesta | ArrancaYa',
    description: 'Descubre cuánto cuesta cambiar la batería del coche en Barcelona en 2026. Precios por marca, dónde cambiarla y alternativas más baratas. Guía completa.',
    canonical: 'https://arrancaya-bcn.es/blog/precio-cambio-bateria-barcelona'
  });

  useEffect(() => {
    const metaKeywords = document.querySelector('meta[name="keywords"]') || document.createElement('meta');
    metaKeywords.name = 'keywords';
    metaKeywords.content = 'precio cambio batería coche, cuánto cuesta batería coche, cambiar batería Barcelona, precio batería 2026, batería coche barata';
    document.head.appendChild(metaKeywords);

    const schema = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Precio Cambio Batería Coche Barcelona 2026",
      "description": "Guía de precios para cambio de batería de coche en Barcelona",
      "author": { "@type": "Organization", "name": "ArrancaYa BCN" },
      "datePublished": "2026-01-20",
      "dateModified": "2026-01-20"
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'event': 'page_view',
      'page_title': 'Precio Cambio Batería Barcelona',
      'content_group1': 'Blog'
    });

    return () => {
      document.head.removeChild(script);
      if (metaKeywords.parentNode) document.head.removeChild(metaKeywords);
    };
  }, []);

  return (
    <div className="min-h-screen bg-obsidian">
      {/* Navigation */}
      <div className="bg-obsidian/90 backdrop-blur-xl border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link 
            to="/blog" 
            className="inline-flex items-center text-neon hover:text-neon/80 font-medium transition-colors group"
            data-testid="back-to-blog-link"
          >
            <ArrowLeft className="h-4 w-4 mr-2 transition-transform group-hover:-translate-x-1" />
            Volver al Blog
          </Link>
        </div>
      </div>

      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <header className="mb-12">
          <div className="flex items-center space-x-4 text-sm text-white/60 mb-6">
            <span className="bg-blue-500/20 text-blue-400 px-3 py-1 rounded-sm font-medium uppercase tracking-wider text-xs">Precios 2026</span>
            <span>•</span>
            <span>20 de enero, 2026</span>
            <span>•</span>
            <span>6 min de lectura</span>
          </div>
          
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-black text-white mb-8 tracking-tighter leading-tight">
            Precio Cambio Batería
            <span className="text-neon"> Barcelona 2026</span>
          </h1>
          
          <div className="bg-white/5 border border-white/10 rounded-sm p-8">
            <p className="text-xl text-white/80 leading-relaxed">
              ¿Cuánto cuesta cambiar la batería del coche en Barcelona? Aquí tienes una <strong className="text-white">guía completa de precios</strong>, 
              dónde hacerlo y una alternativa más económica si tu batería solo está descargada.
            </p>
          </div>
        </header>

        {/* Content */}
        <div className="space-y-12">
          
          {/* Tabla de precios */}
          <section className="bg-white/5 border border-white/10 rounded-sm p-8">
            <h2 className="font-heading text-2xl font-bold text-white mb-6 flex items-center">
              <Euro className="h-6 w-6 text-neon mr-3" />
              Precios Orientativos 2026
            </h2>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-3 px-4 text-white/60 font-medium text-sm">Tipo de Batería</th>
                    <th className="text-center py-3 px-4 text-white/60 font-medium text-sm">Solo Batería</th>
                    <th className="text-center py-3 px-4 text-white/60 font-medium text-sm">Con Instalación</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { type: "Batería estándar (45-60Ah)", only: "60-90€", install: "80-120€" },
                    { type: "Batería media (60-75Ah)", only: "90-130€", install: "120-170€" },
                    { type: "Batería grande (75-100Ah)", only: "130-180€", install: "170-230€" },
                    { type: "Batería AGM (Start-Stop)", only: "150-250€", install: "200-300€" },
                    { type: "Batería Premium (BMW, Mercedes)", only: "200-400€", install: "280-500€" }
                  ].map((row, idx) => (
                    <tr key={idx} className="border-b border-white/5 hover:bg-white/5">
                      <td className="py-3 px-4 text-white text-sm">{row.type}</td>
                      <td className="py-3 px-4 text-neon font-bold text-center">{row.only}</td>
                      <td className="py-3 px-4 text-stark font-bold text-center">{row.install}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <p className="text-white/50 text-sm mt-4">
              * Precios orientativos en Barcelona. Pueden variar según taller y marca específica.
            </p>
          </section>

          {/* Dónde cambiar */}
          <section className="bg-white/5 border border-white/10 rounded-sm p-8">
            <h2 className="font-heading text-2xl font-bold text-white mb-6 flex items-center">
              <Wrench className="h-6 w-6 text-neon mr-3" />
              ¿Dónde Cambiar la Batería en Barcelona?
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              {[
                { 
                  place: "Concesionario oficial",
                  pros: ["Batería original", "Garantía completa"],
                  cons: ["Más caro (+30-50%)", "Puede requerir cita"],
                  price: "€€€"
                },
                { 
                  place: "Taller mecánico independiente",
                  pros: ["Precio competitivo", "Rapidez"],
                  cons: ["Calidad variable", "Garantía limitada"],
                  price: "€€"
                },
                { 
                  place: "Centros especializados (Norauto, Midas...)",
                  pros: ["Sin cita", "Garantía decente"],
                  cons: ["Producto estándar", "Upselling"],
                  price: "€€"
                },
                { 
                  place: "Comprar online + instalar tú",
                  pros: ["Más barato", "Eliges la marca"],
                  cons: ["Necesitas conocimientos", "Reciclaje batería vieja"],
                  price: "€"
                }
              ].map((option, idx) => (
                <div key={idx} className="bg-obsidian border border-white/10 rounded-sm p-4">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="font-bold text-white">{option.place}</h3>
                    <span className="text-neon font-bold">{option.price}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <p className="text-neon text-xs mb-1">Ventajas:</p>
                      {option.pros.map((pro, i) => (
                        <p key={i} className="text-white/60">+ {pro}</p>
                      ))}
                    </div>
                    <div>
                      <p className="text-stark text-xs mb-1">Desventajas:</p>
                      {option.cons.map((con, i) => (
                        <p key={i} className="text-white/60">- {con}</p>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Antes de cambiar */}
          <section className="bg-neon/10 border border-neon/30 rounded-sm p-8">
            <h2 className="font-heading text-2xl font-bold text-neon mb-6 flex items-center">
              <AlertTriangle className="h-6 w-6 mr-3" />
              ¡ESPERA! Antes de Cambiar la Batería...
            </h2>
            
            <div className="bg-obsidian border border-neon/30 rounded-sm p-6 mb-6">
              <p className="text-white text-lg leading-relaxed">
                <strong className="text-neon">El 70% de los coches que "necesitan batería nueva" solo tienen la batería descargada.</strong>
              </p>
              <p className="text-white/70 mt-3">
                Si tu coche no arranca pero la batería tiene menos de 4-5 años, probablemente solo necesitas un arranque. 
                No gastes 150-300€ si con 69€ puedes seguir circulando.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-obsidian border border-white/10 rounded-sm p-4">
                <h3 className="font-bold text-stark mb-2">Cambio de batería</h3>
                <p className="text-3xl font-black text-stark">150-300€</p>
                <p className="text-white/50 text-sm">Media en Barcelona</p>
              </div>
              <div className="bg-obsidian border border-neon/30 rounded-sm p-4">
                <h3 className="font-bold text-neon mb-2">Arranque ArrancaYa</h3>
                <p className="text-3xl font-black text-neon">69-99€</p>
                <p className="text-white/50 text-sm">Y sigues circulando</p>
              </div>
            </div>
          </section>

          {/* Cuándo SÍ cambiar */}
          <section className="bg-white/5 border border-white/10 rounded-sm p-8">
            <h2 className="font-heading text-2xl font-bold text-white mb-6">
              ¿Cuándo SÍ Debes Cambiar la Batería?
            </h2>
            
            <div className="space-y-3">
              {[
                "La batería tiene más de 5-6 años",
                "Se descarga repetidamente en pocos días",
                "Está hinchada o tiene fugas",
                "El taller te lo confirma tras diagnóstico",
                "Aunque la arranques, no mantiene la carga"
              ].map((item, idx) => (
                <div key={idx} className="flex items-center space-x-3 bg-obsidian border border-white/10 rounded-sm p-3">
                  <CheckCircle className="h-5 w-5 text-stark flex-shrink-0" />
                  <span className="text-white/80">{item}</span>
                </div>
              ))}
            </div>
          </section>

          {/* Consejos */}
          <section className="bg-white/5 border border-white/10 rounded-sm p-8">
            <h2 className="font-heading text-2xl font-bold text-white mb-6 flex items-center">
              <Clock className="h-6 w-6 text-neon mr-3" />
              Consejos para Alargar la Vida de tu Batería
            </h2>
            
            <div className="grid md:grid-cols-2 gap-4">
              {[
                { tip: "Conduce regularmente", desc: "Los viajes cortos no recargan bien la batería. Haz trayectos de +20min semanalmente." },
                { tip: "Revisa los terminales", desc: "Límpialos si ves corrosión blanca. Usa agua caliente y bicarbonato." },
                { tip: "Apaga todo antes de salir", desc: "Luces, radio, USB. Un descuido puede costarte caro." },
                { tip: "Cuidado con el frío extremo", desc: "En invierno las baterías sufren más. Considera una funda térmica." }
              ].map((item, idx) => (
                <div key={idx} className="bg-obsidian border border-white/10 rounded-sm p-4">
                  <h4 className="font-bold text-neon mb-2">{item.tip}</h4>
                  <p className="text-white/60 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

        </div>

        {/* CTA */}
        <div className="bg-neon text-obsidian rounded-sm p-8 mt-12">
          <div className="text-center">
            <h2 className="font-heading text-2xl font-black mb-4">¿Tu Coche No Arranca?</h2>
            <p className="text-obsidian/80 mb-6 max-w-xl mx-auto">
              Antes de gastar 200€ en una batería nueva, prueba nuestro servicio de arranque. 
              Llegamos en 30 minutos y solo pagas 69€/99€.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/')}
                className="bg-obsidian hover:bg-obsidian/90 text-white px-8 py-3 rounded-sm font-bold"
                data-testid="cta-whatsapp-btn"
              >
                <Zap className="h-5 w-5 mr-2" />
                Probar Arranque Primero
              </Button>
              <Button
                onClick={() => window.open('tel:+34613076099')}
                variant="outline"
                className="border-2 border-obsidian text-obsidian hover:bg-obsidian hover:text-white px-8 py-3 rounded-sm font-bold"
                data-testid="cta-phone-btn"
              >
                <Phone className="h-5 w-5 mr-2" />
                613 076 099
              </Button>
            </div>
          </div>
        </div>
      </article>
    </div>
  );
};

export default PrecioCambioBateriaBarcelona;
